/**
 * Index page designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_project_pages`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const indexPageDesignsPack: ToilePack = {
    slug: 'index-page-designs',
    category: 'classroom',
    name: 'Index page designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'table', label: 'Table index', subject: 'an index page with columns "S.No.", "Topic", "Page No.", "Sign" and a decorated heading "INDEX"'},
        {key: 'doodle', label: 'Doodle index', subject: 'an index page with a doodle border and empty rows'},
        {key: 'ruled', label: 'Ruled', subject: 'a ruled index page with a ribbon heading'},
    ],
    styles: [
        {key: 'sketch', label: 'Sketch pens', style: 'outlined in black fineliner and filled in with bright sketch pens in flat even colour, neat hand-drawn lines with a slight wobble', palette: 'bright blues, greens, oranges and pinks', framing: 'centred on a white A4 sheet seen straight on with an even clean margin all round'},
        {key: 'pencil', label: 'Colour pencils', style: 'drawn in sharp colour pencils with soft layered shading and visible pencil strokes, a gentle hand-coloured finish', palette: 'soft pencil tones of sky blue, leaf green and peach', framing: 'filling a white A4 sheet seen straight on, a narrow pencil-ruled margin line inside the edge of the page'},
        {key: 'duo', label: 'Two-colour pen', style: 'drawn in black gel pen with one accent colour of highlighter, clean ink lines, minimal and tidy', palette: 'black ink with blue highlighter only', framing: 'on a cream notebook page seen straight on with a generous margin'},
    ],
    directions: ['with border', 'colourful', 'simple'],
    directionHint: 'try: with border',
    seo: {
        title: 'Index page designs: table, doodle, ruled',
        metaDescription: 'Nine hand-drawn index pages for projects and notebooks: table, doodle and ruled designs, in three pen styles.',
        h1: 'Index page designs: table, doodle, ruled',
        tagline: 'Nine index pages: table, doodle and ruled, each in three pen styles.',
        intro: 'Index pages for a project file, assignment or notebook, drawn by hand with pens and colours. The table index has columns for S.No., Topic, Page No. and Sign under an INDEX heading. The doodle index has a doodle border and empty rows. The ruled index has ruled lines and a ribbon heading. Each comes in three pen styles. Type a twist, such as with border, colourful or simple, and the same three layouts can take it.',
        useCases: [
            'the index page of a project file',
            'a contents list for an assignment',
            'a page to list topics in a notebook',
        ],
        faq: [
            {q: 'What columns does the table index have?', a: 'S.No., Topic, Page No. and Sign.'},
            {q: 'Are the rows filled in?', a: 'No. The lines are left blank for you to write in.'},
            {q: 'What is the difference between the three designs?', a: 'The table index has ruled columns. The doodle index has a doodle border around empty rows. The ruled index has a ribbon heading.'},
            {q: 'What are the three styles?', a: 'Sketch pens use bright flat colour inside black fineliner outlines. Colour pencils give soft layered shading in pastel tones. Two-colour pen is black gel ink with one highlighter colour.'},
        ],
    },
}
